import {
  DocumentTextIcon,
  RectangleStackIcon,
  Squares2X2Icon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import type { ComponentType, SVGProps } from 'react';
import type { FormSchemaV2, SectionKind } from '../../lib/agentShared/formSchema';
import { Badge, Cluster, ContextHeader, Stack, Surface, ToolbarActions } from '../ui';

export type TemplateEditorSelection =
  | { kind: 'template' }
  | { kind: 'section'; sectionId: string }
  | { kind: 'field'; sectionId: string; fieldId: string };

export interface TemplateEditorOutlineProps {
  schema: FormSchemaV2;
  selection: TemplateEditorSelection;
  fieldErrors?: Record<string, string[]>;
  onSelect: (selection: TemplateEditorSelection) => void;
}

const SECTION_ICON: Record<SectionKind, ComponentType<SVGProps<SVGSVGElement>>> = {
  section: Squares2X2Icon,
  page: RectangleStackIcon,
  table: TableCellsIcon,
};

const selectionActionClass =
  'min-w-0 flex-1 [&>button]:min-w-0 [&>button]:max-w-full [&>button]:overflow-hidden [&>button]:text-ellipsis';

/**
 * Document outline, intentionally section-level at rest. Questions remain
 * discoverable on the paper and in the focused inspector without turning
 * the navigation rail into a second copy of the form.
 */
export function TemplateEditorOutline({
  schema,
  selection,
  fieldErrors = {},
  onSelect,
}: TemplateEditorOutlineProps): JSX.Element {
  const questionCount = schema.sections.reduce((total, section) => total + section.fields.length, 0);

  return (
    <Surface
      as="section"
      variant="bare"
      className="min-h-full"
      data-testid="template-editor-outline"
    >
      <Surface as="section" variant="flat" padding="sm" className="rounded-none border-x-0 border-t-0">
        <ContextHeader
          title="Outline"
          headingLevel={2}
          action={(
            <span className="text-caption text-muted-foreground">
              {schema.sections.length} sections
            </span>
          )}
        />
      </Surface>

      <Stack as="nav" aria-label="Template outline" gap={4} className="p-3">
        <Stack gap={1}>
          <span className="px-2 text-caption font-semibold uppercase tracking-eyebrow text-muted-foreground">
            Document
          </span>
          <ToolbarActions
            size="sm"
            maxVisible={1}
            className={selectionActionClass}
            actions={[
              {
                id: 'template',
                label: schema.title || 'Untitled template',
                ariaLabel: `Edit template settings for ${schema.title || 'Untitled template'}`,
                icon: <DocumentTextIcon className="h-4 w-4" />,
                variant: selection.kind === 'template' ? 'secondary' : 'ghost',
                pressed: selection.kind === 'template',
                onClick: () => onSelect({ kind: 'template' }),
                'data-testid': 'outline-template',
              },
            ]}
          />
          <span className="px-2 text-caption text-muted-foreground">
            {questionCount} fields
          </span>
        </Stack>

        <Stack gap={1}>
          <span className="px-2 text-caption font-semibold uppercase tracking-eyebrow text-muted-foreground">
            Structure
          </span>
          {schema.sections.map((section, index) => {
            const kind = section.kind ?? 'section';
            const SectionIcon = SECTION_ICON[kind];
            const selected = selection.kind !== 'template' && selection.sectionId === section.id;
            const errorCount = section.fields.reduce(
              (total, field) => total + (fieldErrors[field.id]?.length ?? 0),
              0,
            );

            return (
              <Cluster key={section.id} justify="between" align="center" gap={1}>
                <ToolbarActions
                  size="sm"
                  maxVisible={1}
                  className={selectionActionClass}
                  actions={[
                    {
                      id: section.id,
                      label: section.name || `${kind} ${index + 1}`,
                      ariaLabel: `Select ${kind} ${section.name || index + 1}`,
                      icon: <SectionIcon className="h-4 w-4" />,
                      variant: selected ? 'secondary' : 'ghost',
                      pressed: selected,
                      onClick: () => onSelect({ kind: 'section', sectionId: section.id }),
                      'data-testid': `outline-section-${section.id}`,
                    },
                  ]}
                />
                {errorCount > 0 ? (
                  <Badge tone="destructive" radius="pill" className="shrink-0">
                    {errorCount}
                  </Badge>
                ) : (
                  <span className="shrink-0 px-1 text-caption tabular-nums text-muted-foreground">
                    {section.fields.length}
                  </span>
                )}
              </Cluster>
            );
          })}
        </Stack>
      </Stack>
    </Surface>
  );
}
